import fetchJSON from '@gaia/utils/fetchJSON';
import {ampli} from '@gaia/utils/gaia/trackAnalytics';
import {useEffect} from 'react';
import useSWRImmutable from 'swr/immutable';

interface CurrentConditionProps {
    type: 'current';
    coordinates: number[];
}

interface ForecastProps {
    type: 'forecast';
    coordinates: number[];
}

type WeatherResponse<T extends CurrentConditionProps | ForecastProps> =
    T extends CurrentConditionProps
        ? {
              data: WeatherCondition | undefined;
              error: any;
              isLoading: boolean;
          }
        : {
              data: ForecastedWeatherData | undefined;
              error: any;
              isLoading: boolean;
          };

const PROD_URL = 'https://weather.gaiagps.xyz/';

export default function useWeatherAPI<T extends CurrentConditionProps | ForecastProps>({
    type,
    coordinates,
}: T): WeatherResponse<T> {
    const token = useWeatherToken();

    const [longitude, latitude] = coordinates;
    const truncatedLat = latitude.toFixed(3);
    const truncatedLng = longitude.toFixed(3);
    const query = `${type}?lat=${truncatedLat}&lon=${truncatedLng}`;
    const url = `${PROD_URL}${query}`;
    const {data, error, isLoading} = useSWRImmutable(
        [query, token],
        async () => {
            if (!token) return;
            const {weather_data} = await fetchJSON(url, {
                method: 'GET',
                headers: {
                    Authorization: `Bearer ${token}`,
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                },
            });

            if (type === 'current') {
                return weather_data[0].data.current;
            }
            return weather_data[0];
        },
        {
            onErrorRetry: (err) => {
                // the API returns 404 when weather is not available for the provided location - do not retry
                if (err.status === 404) return;
            },
        }
    );

    // "Fetch Weather" Itly analytics: if hasError, report isWeatherAvailable: false
    const hasError = error !== undefined;
    const hasData = data !== undefined;

    useEffect(() => {
        if (type === 'forecast' && (hasError || hasData)) {
            ampli.fetchWeather({
                isWeatherAvailable: hasData,
                weatherRequestType: type,
                latitude,
                longitude,
            });
        }
    }, [hasError, hasData, latitude, longitude, type]);

    return {
        data,
        error,
        isLoading: isLoading && !error && !data,
    };
}

type WeatherToken = {
    token: string | null;
    expires: string | null;
};

const WEATHER_TOKEN_ENDPOINT = '/oauth/jwt/';

export function isTokenExpired(expires: WeatherToken['expires']) {
    if (!expires) return true;
    const tokenExpiry = new Date(expires).getTime();
    return tokenExpiry <= Date.now();
}

function useWeatherToken() {
    const {data, isValidating, mutate} = useSWRImmutable<WeatherToken>(
        WEATHER_TOKEN_ENDPOINT,
        fetchJSON,
        {
            fallbackData: {
                token: null,
                expires: null,
            },
        }
    );

    const {token, expires} = data!;

    const isExpired = isTokenExpired(expires);

    if (isExpired && !isValidating) mutate().catch(console.error);

    return !isExpired ? token : null;
}
